import { ChatMessage, ParsedChatMessage } from '@trovata/app/features/ai-agents/models/agents.model';

const FINPROMPT_OPEN: string = '<finprompt>';
const FINPROMPT_CLOSE: string = '</finprompt>';
const FINPROMPT_REGEX: RegExp = /<finprompt>([\s\S]*?)<\/finprompt>/;
const FINPROMPT_BLOCK_REGEX: RegExp = /<finprompt>[\s\S]*?<\/finprompt>/g;
const BOLD_NAME_REGEX: RegExp = /\*\*(.+?)\*\*/;

/**
 * Parses a single chat message, extracting any `<finprompt>` tag content.
 * Returns a ParsedChatMessage with the finprompt extracted and display text cleaned.
 *
 * @param message - The chat message to parse
 * @returns Parsed message with extracted finprompt and cleaned text
 */
export function parseChatMessage(message: ChatMessage): ParsedChatMessage {
	if (!message?.content) {
		return { ...message, textContent: '', finprompt: undefined };
	}

	const finpromptMatch: RegExpMatchArray | null = message.content.match(FINPROMPT_REGEX);
	const finprompt: string | undefined = finpromptMatch ? finpromptMatch[1].trim() : undefined;
	const textContent: string = message.content.replace(FINPROMPT_REGEX, '').trim();

	return { ...message, finprompt, textContent };
}

/**
 * Extracts an agent name from the AI response text.
 * Looks for bold markdown patterns (e.g., **Agent Name**).
 * Returns empty string when no bold name is found.
 *
 * @param textContent - The cleaned AI response text (finprompt tags removed)
 * @returns Extracted agent name, or empty string if none found
 */
export function extractAgentName(textContent: string): string {
	if (!textContent) {
		return '';
	}
	const boldMatch: RegExpMatchArray | null = textContent.match(BOLD_NAME_REGEX);
	if (boldMatch) {
		return boldMatch[1].trim();
	}
	return '';
}

/**
 * Parses an entire conversation history.
 * Used when loading an existing agent with history (GET /agent/?id=).
 *
 * @param messages - Array of chat messages to parse
 * @returns Array of parsed messages
 */
export function parseAllMessages(messages: readonly ChatMessage[]): ParsedChatMessage[] {
	return messages.map((message: ChatMessage) => parseChatMessage(message));
}

/**
 * Returns the chat-visible portion of a streaming buffer with finprompt content removed.
 * Closed `<finprompt>...</finprompt>` blocks are stripped entirely; an unclosed
 * `<finprompt>` truncates the visible content from the open tag onward.
 *
 * Used during streaming so the chat bubble doesn't flash the raw finprompt body
 * before it gets routed into the instructions textarea.
 *
 * @param rawContent - The full accumulated stream content
 * @returns The chat-visible portion with finprompt content removed
 */
export function extractDisplayChat(rawContent: string): string {
	if (!rawContent) {
		return '';
	}
	const withoutClosedBlocks: string = rawContent.replace(FINPROMPT_BLOCK_REGEX, '');
	const openIdx: number = withoutClosedBlocks.indexOf(FINPROMPT_OPEN);
	return openIdx === -1 ? withoutClosedBlocks : withoutClosedBlocks.slice(0, openIdx);
}

/**
 * Returns the partial-or-complete content inside the first `<finprompt>` block of
 * a streaming buffer. Returns null when no finprompt block has started yet.
 *
 * Used during streaming to live-update `configForm.instructions` as deltas arrive,
 * so the textarea fills in real-time instead of populating only on `complete`.
 *
 * @param rawContent - The full accumulated stream content
 * @returns The (partial) finprompt body, or null if not yet started
 */
export function extractStreamingFinprompt(rawContent: string): string | null {
	if (!rawContent) {
		return null;
	}
	const openIdx: number = rawContent.indexOf(FINPROMPT_OPEN);
	if (openIdx === -1) {
		return null;
	}
	const afterOpen: string = rawContent.slice(openIdx + FINPROMPT_OPEN.length);
	const closeIdx: number = afterOpen.indexOf(FINPROMPT_CLOSE);
	return closeIdx === -1 ? afterOpen : afterOpen.slice(0, closeIdx);
}
